import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BackgroundResponse } from 'components/BackgroundComponent/types';
import { PersonsResponse } from 'components/PersonComponent/types';
import { RootState } from '../store';

export interface ResourcesState {
  bgLoaded: boolean;
  bgLoading: boolean;
  soundsLoaded: boolean;
  soundsLoading: boolean;
  personsLoaded: boolean;
  personsLoading: boolean;
  error: boolean;
  bgList: BackgroundResponse[];
  personList: Record<string, PersonsResponse>;
  soundList: BackgroundResponse[];
}

const initialState: ResourcesState = {
  bgLoaded: false,
  bgLoading: true,
  soundsLoaded: false,
  soundsLoading: true,
  personsLoaded: false,
  personsLoading: true,
  error: false,
  bgList: [],
  personList: {},
  soundList: [],
};

export const loadSounds = createAsyncThunk('resources/loadSounds', async () => {
  if (window.novellStory && window.novellStory.sound) {
    if (typeof window.novellStory.sound === 'string') {
      const res = await fetch(window.novellStory.sound);
      if (res.status !== 200) {
        throw new Error();
      }
      const SOUND = (await res.json()) as BackgroundResponse[];
      return SOUND;
    }
    return window.novellStory.sound;
  }
  throw new Error();
});

export const loadBackgrounds = createAsyncThunk('resources/loadBackgrounds', async () => {
  if (window.novellStory && window.novellStory.bg) {
    if (typeof window.novellStory.bg === 'string') {
      const res = await fetch(window.novellStory.bg);
      if (res.status !== 200) {
        throw new Error();
      }
      const BG = (await res.json()) as BackgroundResponse[];
      return BG;
    }
    return window.novellStory.bg;
  }
  throw new Error();
});

export const loadPersons = createAsyncThunk('resources/loadPersons', async () => {
  if (window.novellStory && window.novellStory.person) {
    if (typeof window.novellStory.person === 'string') {
      const res = await fetch(window.novellStory.person);
      if (res.status !== 200) {
        throw new Error();
      }
      const PERSONS = (await res.json()) as PersonsResponse[];

      return PERSONS;
    }
    return window.novellStory.person;
  }
  throw new Error();
});

export const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  extraReducers: {
    [loadBackgrounds.pending.type]: (state) => {
      state.bgLoading = true;
    },
    [loadBackgrounds.rejected.type]: (state) => {
      state.error = true;
    },
    [loadBackgrounds.fulfilled.type]: (state, action) => {
      state.bgList = action.payload;
      state.bgLoading = false;
    },
    [loadSounds.pending.type]: (state) => {
      state.soundsLoading = true;
    },
    [loadSounds.rejected.type]: (state) => {
      state.error = true;
    },
    [loadSounds.fulfilled.type]: (state, action) => {
      state.soundList = action.payload;
      state.soundsLoading = false;
    },
    [loadPersons.rejected.type]: (state) => {
      state.error = true;
    },
    [loadPersons.pending.type]: (state) => {
      state.personsLoading = true;
    },
    [loadPersons.fulfilled.type]: (state, action) => {
      state.personList = action.payload;
      state.personsLoading = false;
    },
  },
  reducers: {
    setBGLoaded: (state, action: PayloadAction<boolean>) => {
      state.bgLoaded = action.payload;
    },
    setPersonsLoaded: (state, action: PayloadAction<boolean>) => {
      state.personsLoaded = action.payload;
    },
    setSoundLoaded: (state, action: PayloadAction<boolean>) => {
      state.soundsLoaded = action.payload;
    },
    setError: (state) => {
      state.error = true;
    },
  },
});

export const { setBGLoaded, setPersonsLoaded, setSoundLoaded, setError } = resourcesSlice.actions;

export const getBGLoaded = (state: RootState) => state.resources.bgLoaded;
export const getBGLoading = (state: RootState) => state.resources.bgLoading;
export const getBGList = (state: RootState) => state.resources.bgList;

export const getSoundLoaded = (state: RootState) => state.resources.soundsLoaded;
export const getSoundLoading = (state: RootState) => state.resources.soundsLoading;
export const getSoundList = (state: RootState) => state.resources.soundList;

export const getPersonsLoaded = (state: RootState) => state.resources.personsLoaded;
export const getPersonsLoading = (state: RootState) => state.resources.personsLoading;
export const getPersonsList = (state: RootState) => state.resources.personList;
export const getError = (state: RootState) => state.resources.error;

export const resourcesReducer = resourcesSlice.reducer;
